import React, { useEffect, useRef } from "react";
import { Box } from "@mui/material";

interface Particle {
    x: number;
    y: number;
    radius: number;
    vx: number;
    vy: number;
    alpha: number;
}

interface InactiveSunshineProps {
    isActive: boolean;
}

type Point = {
    x: number;
    y: number;
};

export default function InactiveSunshine({ isActive }: InactiveSunshineProps) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const requestRef = useRef<number | null>(null);
    const particlesRef = useRef<Particle[]>([]);
    const rotationRef = useRef<number>(0);

    useEffect(() => {
        if (!isActive || !canvasRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
            initParticles();
        };

        const initParticles = () => {
            const particles: Particle[] = [];
            const numParticles = 50;
            for (let i = 0; i < numParticles; i++) {
                particles.push({
                    x: Math.random() * canvas.width,
                    y: Math.random() * canvas.height,
                    radius: Math.random() * 3,
                    vx: (Math.random() - 0.5) * 0.5,
                    vy: (Math.random() - 0.5) * 0.5,
                    alpha: Math.random() * 0.5 + 0.1,
                });
            }
            particlesRef.current = particles;
        };

        resizeCanvas();
        window.addEventListener("resize", resizeCanvas);

        const animate = () => {
            if (!ctx || !canvas) return;
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Draw Sun Rays (Rotating)
            // Center of sun at top right, slightly off screen
            const sunX = canvas.width * 0.85;
            const sunY = canvas.height * 0.15;
            const time = performance.now() * 0.001;

            ctx.save();
            ctx.translate(sunX, sunY);
            rotationRef.current += 0.002;
            ctx.rotate(rotationRef.current);
            ctx.globalCompositeOperation = "screen";

            // Draw rays
            const numRays = 12;
            const rayLength = Math.max(canvas.width, canvas.height) * 1.1;
            const rayWidthAtCenter = 26;
            const rayWidthAtEnd = 145;
            const raySegments = 34;
            for (let i = 0; i < numRays; i++) {
                const angle = ((Math.PI * 2) / numRays) * i;
                const pulse = 0.95 + 0.16 * Math.sin(time * 1.5 + i * 0.55);
                const wavePhase = time * 2.4 + i * 0.9;
                const waveFrequency = 2.8;
                const leftSide: Point[] = [];
                const rightSide: Point[] = [];

                ctx.save();
                ctx.rotate(angle);

                ctx.beginPath();
                for (let segment = 0; segment <= raySegments; segment++) {
                    const t = segment / raySegments;
                    const y = t * rayLength;
                    const baseWidth =
                        rayWidthAtCenter +
                        (rayWidthAtEnd - rayWidthAtCenter) * t * pulse;
                    const envelope = 0.2 + 0.8 * Math.pow(Math.sin(Math.PI * t), 1.15);
                    const widthWobble =
                        Math.sin(t * Math.PI * 2 * (waveFrequency * 1.2) + wavePhase * 0.8) *
                        9 *
                        envelope;
                    const width = baseWidth + widthWobble;

                    // Independent left/right wave shapes keep long edges organic.
                    const axisDrift =
                        Math.sin(time * 1.15 + i * 0.75) * 7 * envelope;
                    const leftWave =
                        Math.sin(t * Math.PI * 2 * waveFrequency + wavePhase) *
                        (13 + 4 * pulse) *
                        envelope;
                    const leftRipple =
                        Math.sin(t * Math.PI * 2 * (waveFrequency * 1.55) + wavePhase * 1.25) *
                        6 *
                        envelope;
                    const rightWave =
                        Math.sin(t * Math.PI * 2 * (waveFrequency * 0.9) + wavePhase + 1.2) *
                        (11 + 3 * pulse) *
                        envelope;
                    const rightRipple =
                        Math.cos(t * Math.PI * 2 * (waveFrequency * 1.75) + wavePhase * 1.05) *
                        5 *
                        envelope;

                    leftSide.push({
                        x: axisDrift - width + leftWave + leftRipple,
                        y,
                    });
                    rightSide.push({
                        x: axisDrift + width + rightWave + rightRipple,
                        y,
                    });
                }

                // Draw segments with per-segment gradient to ensure soft edges
                for (let j = 0; j < raySegments; j++) {
                    const p1 = leftSide[j];
                    const p2 = rightSide[j];
                    const p3 = rightSide[j + 1];
                    const p4 = leftSide[j + 1];

                    ctx.beginPath();
                    ctx.moveTo(p1.x, p1.y);
                    ctx.lineTo(p2.x, p2.y);
                    ctx.lineTo(p3.x, p3.y);
                    ctx.lineTo(p4.x, p4.y);
                    ctx.closePath();

                    // Calculate gradient width for this segment
                    const leftX = (p1.x + p4.x) / 2;
                    const rightX = (p2.x + p3.x) / 2;

                    const segmentGradient = ctx.createLinearGradient(leftX, 0, rightX, 0);
                    segmentGradient.addColorStop(0, "rgba(255, 215, 0, 0)");
                    segmentGradient.addColorStop(0.16, "rgba(255, 214, 112, 0.12)");
                    segmentGradient.addColorStop(0.34, "rgba(255, 233, 156, 0.3)");
                    segmentGradient.addColorStop(0.5, "rgba(255, 248, 198, 0.44)");
                    segmentGradient.addColorStop(0.66, "rgba(255, 233, 156, 0.3)");
                    segmentGradient.addColorStop(0.84, "rgba(255, 214, 112, 0.12)");
                    segmentGradient.addColorStop(1, "rgba(255, 215, 0, 0)");

                    ctx.fillStyle = segmentGradient;
                    ctx.fill();
                }

                ctx.restore();
            }
            ctx.restore();

            // Draw Sun Glow
            const glowRadius = 470;
            const gradient = ctx.createRadialGradient(sunX, sunY, 0, sunX, sunY, glowRadius);
            gradient.addColorStop(0, "rgba(255, 236, 128, 0.55)"); // Bright yellow center
            gradient.addColorStop(0.36, "rgba(255, 187, 76, 0.2)"); // Warm middle
            gradient.addColorStop(1, "rgba(255, 255, 255, 0)"); // Fade out

            ctx.beginPath();
            ctx.arc(sunX, sunY, glowRadius, 0, Math.PI * 2);
            ctx.fillStyle = gradient;
            ctx.fill();

            // Update and draw floating dust particles
            particlesRef.current.forEach((p) => {
                p.x += p.vx;
                p.y += p.vy;

                // Wrap around screen
                if (p.x < 0) p.x = canvas.width;
                if (p.x > canvas.width) p.x = 0;
                if (p.y < 0) p.y = canvas.height;
                if (p.y > canvas.height) p.y = 0;

                ctx.beginPath();
                ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(255, 215, 0, ${p.alpha})`;
                ctx.fill();

                // Twinkle/fade
                p.alpha += (Math.random() - 0.5) * 0.02;
                if (p.alpha < 0.1) p.alpha = 0.1;
                if (p.alpha > 0.6) p.alpha = 0.6;
            });

            requestRef.current = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            window.removeEventListener("resize", resizeCanvas);
            if (requestRef.current) cancelAnimationFrame(requestRef.current);
        };
    }, [isActive]);

    if (!isActive) return null;

    return (
        <Box
            sx={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                zIndex: 9999,
                pointerEvents: "none",
                opacity: isActive ? 1 : 0,
                transition: "opacity 1s ease-in-out",
                background: "transparent",
            }}
        >
            <canvas
                ref={canvasRef}
                style={{ width: "100%", height: "100%", display: "block" }}
            />
        </Box>
    );
}
